import { useEffect, useRef } from 'react';
import L from 'leaflet';

const boatIcon = (active) =>
  L.divIcon({ className: '', html: `<div class="ll-boat ${active ? 'active' : ''}">🚤</div>`, iconSize: [30, 30], iconAnchor: [15, 15] });
const pinIcon = (color, glyph) =>
  L.divIcon({ className: '', html: `<div class="ll-pin" style="background:${color}"><span>${glyph}</span></div>`, iconSize: [28, 28], iconAnchor: [14, 28] });
const placeIcon = L.divIcon({ className: '', html: `<div class="ll-dot" style="background:#0e7490"></div>`, iconSize: [12, 12], iconAnchor: [6, 6] });

// Smith Mountain Lake sits roughly between these coords.
const SML_CENTER = [37.055, -79.63];

export default function LakeMap({
  places = [], captains = [], pickup, dropoff, route, activeCaptainId,
  onMapClick, onPlaceClick, onCaptainClick, height = 480,
}) {
  const elRef = useRef(null);
  const mapRef = useRef(null);
  const layersRef = useRef({});
  const cbRef = useRef({});
  cbRef.current = { onMapClick, onPlaceClick, onCaptainClick }; // always-fresh callbacks

  // init once
  useEffect(() => {
    if (mapRef.current || !elRef.current) return;
    const map = L.map(elRef.current, { center: SML_CENTER, zoom: 12, scrollWheelZoom: true, zoomControl: true });
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19, attribution: '© OpenStreetMap',
    }).addTo(map);
    map.on('click', (e) => cbRef.current.onMapClick?.({ lat: e.latlng.lat, lng: e.latlng.lng }));
    mapRef.current = map;
    layersRef.current = {
      places: L.layerGroup().addTo(map),
      boats: L.layerGroup().addTo(map),
      pts: L.layerGroup().addTo(map),
      route: L.layerGroup().addTo(map),
    };
    const ro = new ResizeObserver(() => map.invalidateSize());
    ro.observe(elRef.current);
    setTimeout(() => map.invalidateSize(), 60);
    return () => { ro.disconnect(); map.remove(); mapRef.current = null; };
  }, []);

  // key pickup points
  useEffect(() => {
    const g = layersRef.current.places; if (!g) return;
    g.clearLayers();
    places.forEach((p) => {
      const m = L.marker([p.lat, p.lng], { icon: placeIcon }).bindTooltip(p.name, { direction: 'top', offset: [0, -6] });
      m.on('click', (e) => { L.DomEvent.stopPropagation(e); cbRef.current.onPlaceClick?.(p); });
      g.addLayer(m);
    });
  }, [places]);

  // online captains
  useEffect(() => {
    const g = layersRef.current.boats; if (!g) return;
    g.clearLayers();
    captains.filter((c) => c.online && c.cur_lat != null).forEach((c) => {
      const m = L.marker([c.cur_lat, c.cur_lng], { icon: boatIcon(c.id === activeCaptainId) })
        .bindTooltip(`${c.business_name} · tap to view`, { direction: 'top', offset: [0, -10] });
      m.on('click', (e) => { L.DomEvent.stopPropagation(e); cbRef.current.onCaptainClick?.(c.id); });
      m.addTo(g);
    });
  }, [captains, activeCaptainId]);

  // pickup / dropoff + water route line
  useEffect(() => {
    const gp = layersRef.current.pts, gr = layersRef.current.route; if (!gp) return;
    gp.clearLayers(); gr.clearLayers();
    if (pickup) L.marker([pickup.lat, pickup.lng], { icon: pinIcon('#12a672', 'A') })
      .bindTooltip('Pickup · ' + (pickup.label || 'dropped pin'), { direction: 'top', offset: [0, -22] }).addTo(gp);
    if (dropoff) L.marker([dropoff.lat, dropoff.lng], { icon: pinIcon('#ea8a1e', 'B') })
      .bindTooltip('Dropoff · ' + (dropoff.label || 'dropped pin'), { direction: 'top', offset: [0, -22] }).addTo(gp);
    if (route && route.length > 1) {
      L.polyline(route, { color: '#0e7490', weight: 4, opacity: .9, lineJoin: 'round' }).addTo(gr);
    } else if (pickup && dropoff) {
      L.polyline([[pickup.lat, pickup.lng], [dropoff.lat, dropoff.lng]], { color: '#0e7490', weight: 3, dashArray: '7 7', opacity: .7 }).addTo(gr);
    }
  }, [pickup, dropoff, route]);

  return <div ref={elRef} style={{ height, width: '100%' }} />;
}
